<template>
  <!-- 添加全局背景包装器 -->
  <GlobalBackgroundWrapper :page-key="'coach-reservations'">
    <div class="coach-reservations-container">
      <el-card>
        <div slot="header" class="card-header">
          <h3 class="page-title">预约管理</h3>
          <div class="header-actions">
            <el-button
                icon="el-icon-refresh"
                circle
                @click="handleRefresh"
                :loading="loading"
                title="刷新"
                class="refresh-btn"
            ></el-button>
          </div>
        </div>

        <div class="toolbar">
          <div class="tabs-container">
            <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="reservation-tabs">
              <el-tab-pane label="待确认" name="pending"></el-tab-pane>
              <el-tab-pane label="已确认" name="confirmed"></el-tab-pane>
              <el-tab-pane label="已完成" name="completed"></el-tab-pane>
              <el-tab-pane label="全部" name="all"></el-tab-pane>
            </el-tabs>
          </div>
          <div class="toolbar-actions">
            <el-button
                icon="el-icon-refresh"
                @click="handleRefresh"
                :loading="loading"
                class="refresh-text-btn"
            >
              刷新
            </el-button>
          </div>
        </div>

        <!-- 桌面端表格视图 -->
        <el-table
            :data="reservations"
            v-loading="loading"
            v-if="!isMobile"
            class="reservations-table"
            :default-sort="{prop: 'date', order: 'descending'}"
        >
          <!-- 表格列保持不变 -->
          <el-table-column prop="date" label="预约日期" width="120" sortable>
            <template slot-scope="scope">
              <span class="date-cell">{{ formatDate(scope.row.date) }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="timeSlot" label="时段" width="120">
            <template slot-scope="scope">
              <span class="time-cell">{{ scope.row.timeSlot }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="studentName" label="学员" width="120">
            <template slot-scope="scope">
              <span class="student-cell">{{ scope.row.studentName || '未知学员' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="phone" label="手机号" width="150">
            <template slot-scope="scope">
              <span class="phone-cell">{{ scope.row.phone || '未知号码' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="subject" label="科目" width="100">
            <template slot-scope="scope">
              <el-tag
                  :type="getSubjectType(scope.row.subject)"
                  class="subject-tag"
                  effect="light"
              >
                {{ getSubjectText(scope.row.subject) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag
                  :type="getStatusType(scope.row.status)"
                  class="status-tag"
                  effect="light"
              >
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="220" fixed="right" class-name="actions-column">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                    v-if="scope.row.status === 'pending' || scope.row.status === 'PENDING'"
                    type="text"
                    size="small"
                    @click="confirmReservation(scope.row.id)"
                    class="action-btn confirm-btn"
                >
                  确认
                </el-button>
                <el-button
                    v-if="scope.row.status === 'pending' || scope.row.status === 'PENDING'"
                    type="text"
                    size="small"
                    @click="rejectReservation(scope.row.id)"
                    class="action-btn reject-btn"
                >
                  拒绝
                </el-button>
                <el-button
                    v-if="scope.row.status === 'confirmed' || scope.row.status === 'CONFIRMED'"
                    type="text"
                    size="small"
                    @click="completeReservation(scope.row.id)"
                    class="action-btn complete-btn"
                >
                  完成
                </el-button>
                <el-button
                    type="text"
                    size="small"
                    @click="viewDetails(scope.row)"
                    class="action-btn details-btn"
                >
                  详情
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 移动端卡片视图 -->
        <div v-if="isMobile" class="mobile-reservations-list" v-loading="loading">
          <div v-for="reservation in reservations" :key="reservation.id" class="reservation-card">
            <el-card shadow="hover" class="reservation-card-item">
              <div class="card-header">
                <div class="reservation-info">
                  <h4 class="student-name">{{ reservation.studentName || '未知学员' }}</h4>
                  <div class="status-badges">
                    <el-tag
                        :type="getStatusType(reservation.status)"
                        size="mini"
                        class="status-badge"
                    >
                      {{ getStatusText(reservation.status) }}
                    </el-tag>
                    <el-tag
                        :type="getSubjectType(reservation.subject)"
                        size="mini"
                        class="subject-badge"
                    >
                      {{ getSubjectText(reservation.subject) }}
                    </el-tag>
                  </div>
                </div>
                <div class="reservation-date">
                  {{ formatDate(reservation.date) }}
                </div>
              </div>

              <div class="card-content">
                <div class="info-row">
                  <span class="label">
                    <i class="el-icon-time"></i>
                    时段:
                  </span>
                  <span class="value time">{{ reservation.timeSlot }}</span>
                </div>

                <div class="info-row">
                  <span class="label">
                    <i class="el-icon-phone"></i>
                    手机号:
                  </span>
                  <span class="value phone">{{ reservation.phone || '未知号码' }}</span>
                </div>
              </div>

              <div class="card-actions">
                <el-button
                    v-if="reservation.status === 'pending' || reservation.status === 'PENDING'"
                    type="primary"
                    size="small"
                    @click="confirmReservation(reservation.id)"
                    class="mobile-action-btn confirm-btn"
                >
                  <i class="el-icon-check"></i>
                  确认
                </el-button>
                <el-button
                    v-if="reservation.status === 'pending' || reservation.status === 'PENDING'"
                    type="warning"
                    size="small"
                    @click="rejectReservation(reservation.id)"
                    class="mobile-action-btn reject-btn"
                >
                  <i class="el-icon-close"></i>
                  拒绝
                </el-button>
                <el-button
                    v-if="reservation.status === 'confirmed' || reservation.status === 'CONFIRMED'"
                    type="success"
                    size="small"
                    @click="completeReservation(reservation.id)"
                    class="mobile-action-btn complete-btn"
                >
                  <i class="el-icon-finished"></i>
                  完成
                </el-button>
                <el-button
                    type="text"
                    size="small"
                    @click="viewDetails(reservation)"
                    class="mobile-action-btn details-btn"
                >
                  <i class="el-icon-view"></i>
                  详情
                </el-button>
              </div>
            </el-card>
          </div>
        </div>

        <!-- 空状态提示 -->
        <div v-if="reservations.length === 0 && !loading" class="empty-state">
          <el-empty description="暂无预约数据" class="empty-content">
            <div class="empty-actions">
              <el-button @click="handleRefresh" class="empty-refresh-btn">
                <i class="el-icon-refresh"></i>
                刷新
              </el-button>
            </div>
          </el-empty>
        </div>
      </el-card>
    </div>
  </GlobalBackgroundWrapper>
</template>

<script>
import {coachAPI} from '@/services/api'
import GlobalBackgroundWrapper from '@/components/GlobalBackgroundWrapper.vue'

export default {
  name: 'CoachReservations',
  components: {
    GlobalBackgroundWrapper
  },
  data() {
    return {
      loading: false,
      activeTab: 'pending',
      reservations: [],
      isMobile: false,
      refreshTimer: null
    }
  },
  mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    this.loadReservations()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
  },
  methods: {
    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async loadReservations() {
      this.loading = true
      try {
        const params = this.activeTab === 'all' ? {} : {status: this.activeTab}
        const response = await coachAPI.getReservations(params)
        console.log('原始数据:', response.data)

        // 数据转换 - 根据实际返回的字段名调整
        this.reservations = response.data.map(item => ({
          id: item.id || item.reservationId || item.reservation_id,
          date: item.date || item.reservationDate || item.reservation_date,
          timeSlot: item.timeSlot || item.time_slot,
          studentName: item.studentName || item.student_name ||
              (item.student && item.student.realName) ||
              (item.student && item.student.user && item.student.user.realName),
          phone: item.phone ||
              (item.student && item.student.phone) ||
              (item.student && item.student.user && item.student.user.phone),
          subject: item.subject,
          status: item.status
        }))

        console.log('转换后数据:', this.reservations)
      } catch (error) {
        console.error('加载预约数据失败:', error)
        this.showMessage('加载预约数据失败', 'error')
      } finally {
        this.loading = false
      }
    },

    handleRefresh() {
      // 防止频繁刷新
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.loadReservations();
        this.showMessage('预约列表已刷新', 'success');
      }, 300);
    },

    async confirmReservation(id) {
      try {
        console.log('确认预约 ID:', id) // 调试
        const response = await coachAPI.confirmReservation(id)
        console.log('确认预约响应:', response) // 调试
        this.showMessage('预约已确认', 'success')
        this.loadReservations()
      } catch (error) {
        console.error('确认失败详情:', error)
        console.error('确认失败响应:', error.response) // 调试
        this.showMessage('确认失败: ' + (error.response?.data?.message || error.message || '未知错误'), 'error')
      }
    },

    async rejectReservation(id) {
      try {
        await this.showConfirm('确定要拒绝这个预约吗？', '提示', {
          type: 'warning'
        })

        await coachAPI.rejectReservation(id)
        this.showMessage('预约已拒绝', 'success')
        this.loadReservations()
      } catch (error) {
        // 只处理非取消错误
        if (error !== 'cancel' && error !== 'close') {
          console.error('拒绝失败:', error)
          this.showMessage('操作失败', 'error')
        }
      }
    },

    async completeReservation(id) {
      try {
        await coachAPI.completeReservation(id)
        this.showMessage('标记为已完成', 'success')
        this.loadReservations()
      } catch (error) {
        console.error('完成失败:', error)
        this.showMessage('操作失败', 'error')
      }
    },

    // 响应式消息提示
    showMessage(message, type = 'info') {
      const position = this.isMobile ? 'top' : 'top-right'
      this.$message({
        message,
        type,
        position,
        duration: 3000,
        showClose: true
      })
    },

    // 响应式确认框
    showConfirm(message, title, options = {}) {
      const defaultOptions = {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        // 移动端居中显示，桌面端位置不变
        customClass: this.isMobile ? 'mobile-confirm-dialog' : '',
        // 添加安全的关闭处理
        beforeClose: (action, instance, close) => {
          if (action === 'confirm') {
            instance.confirmButtonLoading = true
            instance.confirmButtonText = '处理中...'

            // 延迟执行关闭，确保动画完成
            setTimeout(() => {
              close()
            }, 100)
          } else {
            close()
          }
        }
      }

      return this.$confirm(message, title, {
        ...defaultOptions,
        ...options
      }).catch(error => {
        // 捕获取消操作，不抛出未处理的错误
        if (error !== 'cancel' && error !== 'close') {
          console.error('确认框错误:', error)
        }
        return Promise.reject(error)
      })
    },

    viewDetails(reservation) {
      const message = `学员: ${reservation.studentName || '未知学员'}\n日期: ${this.formatDate(reservation.date)}\n时段: ${reservation.timeSlot}\n科目: ${this.getSubjectText(reservation.subject)}`

      // 使用响应式弹窗
      this.$alert(message, '预约详情', {
        confirmButtonText: '确定',
        customClass: this.isMobile ? 'mobile-alert-dialog' : '',
        callback: action => {
          if (action === 'confirm') {
            console.log('用户点击了确定按钮')
          }
        }
      }).catch(error => {
        if (error === 'cancel' || error === 'close') {
          return
        }
        console.error('详情对话框错误:', error)
      })
    },

    handleTabClick() {
      this.loadReservations()
    },

    getStatusType(status) {
      // 兼容大小写状态
      const statusUpper = (status || '').toUpperCase()
      const types = {
        'PENDING': 'warning',
        'CONFIRMED': 'success',
        'COMPLETED': 'info',
        'CANCELLED': 'danger',
        'REJECTED': 'danger'
      }
      return types[statusUpper] || 'info'
    },

    getStatusText(status) {
      // 兼容大小写状态
      const statusUpper = (status || '').toUpperCase()
      const texts = {
        'PENDING': '待确认',
        'CONFIRMED': '已确认',
        'COMPLETED': '已完成',
        'CANCELLED': '已取消',
        'REJECTED': '已拒绝'
      }
      return texts[statusUpper] || status
    },

    getSubjectType(subject) {
      // 根据科目返回对应的颜色类型
      const subjectLower = (subject || '').toLowerCase()
      if (subjectLower.includes('subject2') || subjectLower.includes('科目二')) {
        return 'primary'
      } else if (subjectLower.includes('subject3') || subjectLower.includes('科目三')) {
        return 'success'
      } else {
        return 'info'
      }
    },

    getSubjectText(subject) {
      // 兼容不同格式的科目值
      const subjectLower = (subject || '').toLowerCase()
      if (subjectLower.includes('subject2') || subjectLower.includes('科目二')) {
        return '科目二'
      } else if (subjectLower.includes('subject3') || subjectLower.includes('科目三')) {
        return '科目三'
      } else {
        return subject || '未知科目'
      }
    },

    formatDate(date) {
      if (!date) return '无日期'

      try {
        // 处理字符串日期
        if (typeof date === 'string') {
          // 如果是 ISO 格式的日期字符串
          if (date.includes('T')) {
            return new Date(date).toLocaleDateString()
          }
          // 如果是 yyyy-MM-dd 格式
          if (date.includes('-')) {
            const [year, month, day] = date.split('-')
            return `${year}年${month}月${day}日`
          }
          // 其他格式直接显示
          return date
        }

        // 处理 Date 对象
        if (date instanceof Date) {
          return date.toLocaleDateString()
        }

        return '无效日期'
      } catch (error) {
        console.error('日期格式化错误:', error, date)
        return '日期错误'
      }
    }
  }
}
</script>

<style scoped>
/* 原有样式保持不变 */
/* ===== 全局样式变量 ===== */
:root {
  --primary-color: #409EFF;
  --success-color: #67C23A;
  --warning-color: #E6A23C;
  --danger-color: #F56C6C;
  --info-color: #909399;
  --text-primary: #303133;
  --text-regular: #606266;
  --text-secondary: #909399;
  --border-color: #DCDFE6;
  --border-light: #E4E7ED;
  --background-base: #F5F7FA;
  --background-light: #FFFFFF;
  --shadow-light: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  --shadow-medium: 0 2px 8px rgba(0, 0, 0, 0.08);
  --border-radius: 4px;
  --border-radius-lg: 8px;
  --border-radius-xl: 12px;
  --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --transition-fast: all 0.15s ease;
}

/* ===== 基础容器样式 ===== */
.coach-reservations-container {
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.0);
  min-height: calc(100vh - 40px);
}

/* ===== 卡片头部样式 ===== */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.header-actions {
  display: flex;
  align-items: center;
}

.refresh-btn {
  transition: var(--transition);
}

.refresh-btn:hover {
  transform: rotate(180deg);
  background-color: var(--primary-color);
  color: white;
}

/* ===== 工具栏样式 ===== */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.tabs-container {
  flex: 1;
}

.toolbar-actions {
  display: flex;
  align-items: center;
}

.reservation-tabs {
  min-width: 300px;
}

.refresh-text-btn {
  padding: 10px 16px;
  transition: var(--transition);
}

.refresh-text-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-light);
}

/* ===== 表格样式 ===== */
.reservations-table {
  width: 100%;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow-medium);
}

.reservations-table::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 1px;
  background: var(--border-light);
}

/* 表格单元格样式 */
.date-cell, .student-cell {
  font-weight: 500;
  color: var(--text-primary);
}

.time-cell {
  color: var(--text-regular);
}

.phone-cell {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  color: var(--text-regular);
}

.subject-tag, .status-tag {
  font-weight: 500;
  border: none;
}

/* 操作按钮样式 */
.actions-column {
  padding: 8px 0;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  padding: 4px 8px;
  font-size: 13px;
  transition: var(--transition-fast);
  border-radius: var(--border-radius);
  position: relative;
  overflow: hidden;
}

.action-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.action-btn:active::after {
  width: 300px;
  height: 300px;
}

.confirm-btn {
  color: var(--success-color);
}

.confirm-btn:hover {
  background-color: rgba(103, 194, 58, 0.1);
}

.reject-btn {
  color: var(--danger-color);
}

.reject-btn:hover {
  background-color: rgba(245, 108, 108, 0.1);
}

.complete-btn {
  color: var(--primary-color);
}

.complete-btn:hover {
  background-color: rgba(64, 158, 255, 0.1);
}

.details-btn {
  color: var(--info-color);
}

.details-btn:hover {
  background-color: rgba(144, 147, 153, 0.1);
}

/* ===== 移动端卡片样式 ===== */
.mobile-reservations-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reservation-card-item {
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-light);
  transition: var(--transition);
  overflow: hidden;
}

.reservation-card-item:hover {
  box-shadow: var(--shadow-medium);
  transform: translateY(-2px);
}

.reservation-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.reservation-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.student-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
}

.status-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.status-badge, .subject-badge {
  font-weight: 500;
  border: none;
}

.reservation-date {
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light);
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 14px;
  min-width: 80px;
}

.info-row .label i {
  font-size: 14px;
}

.info-row .value {
  color: var(--text-primary);
  font-size: 14px;
  text-align: right;
  flex: 1;
  margin-left: 8px;
  word-break: break-all;
}

.time {
  font-weight: 500;
}

.phone {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-light);
}

.mobile-action-btn {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  height: 36px;
  padding: 8px 4px;
  border-radius: 6px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: var(--transition);
  position: relative;
  overflow: hidden;
}

.mobile-action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.mobile-action-btn:hover::before {
  left: 100%;
}

.mobile-action-btn.confirm-btn {
  background-color: rgba(103, 194, 58, 0.1);
  border-color: var(--success-color);
  color: var(--success-color);
}

.mobile-action-btn.reject-btn {
  background-color: rgba(245, 108, 108, 0.1);
  border-color: var(--danger-color);
  color: var(--danger-color);
}

.mobile-action-btn.complete-btn {
  background-color: rgba(64, 158, 255, 0.1);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.mobile-action-btn.details-btn {
  background-color: rgba(144, 147, 153, 0.1);
  border-color: var(--info-color);
  color: var(--info-color);
}

/* ===== 空状态样式 ===== */
.empty-state {
  padding: 60px 0;
  text-align: center;
}

.empty-content {
  padding: 40px 0;
}

.empty-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.empty-refresh-btn {
  padding: 10px 20px;
}

/* ===== 响应式设计 ===== */
@media (max-width: 768px) {
  .coach-reservations-container {
    padding: 12px 8px;
    background-color: var(--background-light);
  }

  .el-card {
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-medium);
    border: none;
  }

  .el-card__header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  }

  .el-card__body {
    padding: 20px;
  }

  .page-title {
    font-size: 20px;
    font-weight: 600;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
    gap: 16px;
  }

  .tabs-container {
    width: 100%;
  }

  .toolbar-actions {
    width: 100%;
    justify-content: center;
  }

  .refresh-text-btn {
    width: 100%;
    height: 44px;
    font-size: 16px;
    border-radius: var(--border-radius-lg);
  }

  /* 移动端卡片优化 */
  .mobile-reservations-list {
    gap: 12px;
  }

  .reservation-card-item {
    border-radius: var(--border-radius-lg);
    margin-bottom: 0;
    border: 1px solid var(--border-light);
  }

  .reservation-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .reservation-info {
    width: 100%;
  }

  .status-badges {
    width: 100%;
    justify-content: flex-start;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 8px 0;
  }

  .info-row .value {
    text-align: left;
    margin-left: 0;
    width: 100%;
    padding-left: 20px;
  }

  .card-actions {
    flex-direction: column;
    gap: 8px;
  }

  .mobile-action-btn {
    width: 100%;
    height: 44px;
    font-size: 14px;
    border: 1px solid var(--border-light);
    background: var(--background-base);
  }

  .mobile-action-btn:hover {
    background: var(--background-light);
    transform: translateY(-1px);
  }
}

/* ===== 超小屏幕优化 (≤480px) ===== */
@media (max-width: 480px) {
  .coach-reservations-container {
    padding: 8px 4px;
  }

  .el-card__body {
    padding: 16px;
  }

  .page-title {
    font-size: 18px;
  }

  .student-name {
    font-size: 18px;
  }

  .info-row .label {
    font-size: 14px;
    min-width: 70px;
  }

  .info-row .value {
    font-size: 14px;
  }

  .mobile-action-btn {
    height: 48px;
    font-size: 15px;
  }

  /* 空状态优化 */
  .empty-actions {
    flex-direction: column;
    gap: 12px;
  }

  .empty-refresh-btn {
    width: 100%;
    height: 48px;
  }
}

/* ===== 极窄屏幕优化 (≤360px) ===== */
@media (max-width: 360px) {
  .mobile-action-btn {
    font-size: 14px;
    padding: 8px 2px;
  }
}

/* ===== 触摸交互优化 ===== */
@media (max-width: 768px) {
  .el-button:active,
  .el-button:focus {
    transform: scale(0.98);
    transition: transform 0.1s ease;
  }

  .reservation-card-item:active {
    transform: scale(0.995);
    transition: transform 0.1s ease;
  }

  .mobile-action-btn:active {
    transform: scale(0.98);
    background-color: var(--border-light);
  }
}

/* ===== 加载状态优化 ===== */
@media (max-width: 768px) {
  .el-loading-mask {
    border-radius: var(--border-radius-xl);
  }

  .el-loading-spinner .circular {
    width: 32px;
    height: 32px;
  }
}

/* ===== 暗色模式支持 ===== */
/*@media (prefers-color-scheme: dark) {
  :root {
    --text-primary: #E5E7EB;
    --text-regular: #D1D5DB;
    --text-secondary: #9CA3AF;
    --border-color: #4B5563;
    --border-light: #374151;
    --background-base: #111827;
    --background-light: #1F2937;
  }

  .el-card {
    border-color: var(--border-light);
  }

  .reservations-table {
    background-color: var(--background-light);
  }

  .reservation-card-item:hover {
    background-color: #b6c9e8;
  }

  .mobile-action-btn {
    background-color: #374151;
    border-color: var(--border-color);
  }
}*/

/* ===== 动画效果增强 ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.reservation-card-item {
  animation: fadeIn 0.3s ease-out;
}

.mobile-reservations-list .reservation-card:nth-child(1) { animation-delay: 0.05s; }
.mobile-reservations-list .reservation-card:nth-child(2) { animation-delay: 0.1s; }
.mobile-reservations-list .reservation-card:nth-child(3) { animation-delay: 0.15s; }
.mobile-reservations-list .reservation-card:nth-child(4) { animation-delay: 0.2s; }
.mobile-reservations-list .reservation-card:nth-child(5) { animation-delay: 0.25s; }

/* ===== 打印样式优化 ===== */
@media print {
  .toolbar,
  .card-actions,
  .header-actions {
    display: none !important;
  }

  .coach-reservations-container {
    padding: 0;
    background: white;
  }

  .el-card {
    box-shadow: none;
    border: 1px solid #000;
  }
}
</style>

<style>
/* 全局样式 - 移动端消息提示和对话框优化 */
@media (max-width: 768px) {
  .el-message {
    min-width: 90% !important;
    width: auto !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    top: 20px !important;
  }

  .mobile-confirm-dialog,
  .mobile-alert-dialog {
    width: 90% !important;
    max-width: 320px !important;
    margin: 0 auto !important;
  }

  .mobile-confirm-dialog .el-message-box__content,
  .mobile-alert-dialog .el-message-box__content {
    padding: 15px 20px !important;
  }

  .mobile-confirm-dialog .el-message-box__btns,
  .mobile-alert-dialog .el-message-box__btns {
    padding: 10px 20px 15px !important;
  }
}

/* 桌面端消息提示位置优化 */
@media (min-width: 769px) {
  .el-message {
    min-width: 380px !important;
    top: 20px !important;
    right: 20px !important;
  }
}
</style>
